import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { apiClient } from '@/services/api';
import { Card } from '@/components/layout';
import { MainSection } from '@/components/layout/MainSection';
import AdminToolbar from '@/components/AdminToolbar/index';
import LeagueFullSyncPanel from '@/components/LeagueFullSyncPanel';
import { USER_ROLES } from '@/constants';
import { useTranslation } from 'react-i18next';

interface UpdateResult {
  updated: number;
  errors: number;
  errorDetails?: Array<{ league: string; error: string }>;
  message: string;
  league?: string;
  status?: 'completed' | 'failed';
  syncMode?: string;
}

interface SyncStatus {
  isRunning: boolean;
  jobType: string | null;
  startedAt: string | null;
  lastResult: UpdateResult | null;
  lastCompletedAt: string | null;
  timestamp: string;
}

interface League {
  id: number;
  name: string;
  code: string;
  season: string | null;
  country: string | null;
  status: string;
  external_id: string;
}

const MatchesTab: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const isAdmin = Boolean(user?.is_admin === true || user?.role === USER_ROLES.ADMIN);

  const [lastResult, setLastResult] = useState<UpdateResult | null>(null);
  const [prevIsRunning, setPrevIsRunning] = useState(false);

  const {
    data: updateStatus,
    refetch: refetchStatus
  } = useQuery<SyncStatus>({
    queryKey: ['admin-match-update-status'],
    queryFn: async () => {
      const response = await apiClient.get('/admin/matches/update-status');
      return response.data.data.incremental;
    },
    enabled: isAuthenticated && isAdmin,
    refetchInterval: (query) => query.state.data?.isRunning ? 3000 : false,
    refetchOnWindowFocus: false
  });

  const syncRunning = updateStatus?.isRunning;
  const syncLastResult = updateStatus?.lastResult;

  // When sync transitions from running to done, pick up the result
  useEffect(() => {
    if (prevIsRunning && syncRunning === false && syncLastResult) {
      setLastResult(syncLastResult);
    }
    if (syncRunning !== undefined) {
      setPrevIsRunning(syncRunning);
    }
  }, [syncRunning, syncLastResult, prevIsRunning]);

  const { data: competitions } = useQuery({
    queryKey: ['admin-competitions'],
    queryFn: async () => {
      const response = await apiClient.get('/admin/matches/competitions');
      return response.data.data as League[];
    },
    enabled: isAuthenticated && isAdmin
  });

  const updateAllMatchesMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post('/admin/matches/update-all');
      return response.data;
    },
    onSuccess: () => {
      setLastResult(null);
      refetchStatus();
    },
    onError: (error: any) => {
      if (error.response?.status === 409) {
        refetchStatus();
        return;
      }
      setLastResult({
        updated: 0,
        errors: 1,
        message: error.response?.data?.message || error.message || 'Update failed'
      });
    }
  });

  const updateCompetitionMutation = useMutation({
    mutationFn: async (leagueCode: string) => {
      const response = await apiClient.post(`/admin/matches/update-competition/${leagueCode}`);
      return response.data;
    },
    onSuccess: (data) => {
      setLastResult(data.data);
      refetchStatus();
    },
    onError: (error: any) => {
      setLastResult({
        updated: 0,
        errors: 1,
        message: error.response?.data?.message || error.message || 'Competition update failed'
      });
    }
  });

  const fullSyncMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.fullSyncAllMatches();
      return response.data;
    },
    onSuccess: () => {
      setLastResult(null);
      refetchStatus();
    },
    onError: (error: any) => {
      if (error.response?.status === 409) {
        refetchStatus();
        return;
      }
      setLastResult({
        updated: 0,
        errors: 1,
        message: error.response?.data?.message || error.message || 'Full sync failed'
      });
    }
  });

  const handleUpdateAll = () => {
    if (updateStatus?.isRunning) return;
    updateAllMatchesMutation.mutate();
  };

  const handleUpdateCompetition = (leagueCode: string) => {
    if (updateStatus?.isRunning) return;
    updateCompetitionMutation.mutate(leagueCode);
  };

  const handleFullSync = () => {
    if (updateStatus?.isRunning) return;
    const confirmed = window.confirm(
      'Полная синхронизация загрузит ВСЕ матчи за весь сезон для каждой активной лиги. ' +
      'Это тяжёлая операция, которая займёт несколько минут. Продолжить?'
    );
    if (confirmed) {
      fullSyncMutation.mutate();
    }
  };

  const headerRight = updateStatus?.isRunning ? (
    <span className="ls-tag ls-tag--accent ls-flex ls-align-center ls-gap-2">
      <span className="animate-spin" style={{ width: '12px', height: '12px', border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%' }}></span>
      {t('common:loading')}
    </span>
  ) : undefined;

  return (
    <MainSection title={t('admin:matchesAdmin')} headerRight={headerRight}>
      <div className="ls-flex ls-justify-end ls-mb-4">
        <button onClick={() => navigate('/admin/debug-api')} className="ls-btn ls-btn--outlined" style={{ fontSize: '12px' }}>
          🐛 Debug API
        </button>
      </div>

      {/* Admin Toolbar */}
      <AdminToolbar />

      {/* Status Card */}
      <Card className="ls-mb-4">
        <h2 className="ls-h4 ls-mb-3">Статус обновлений</h2>
        <div className="ls-grid ls-col-2 ls-gap-3">
          <div className="ls-flex ls-align-center ls-gap-2">
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: updateStatus?.isRunning ? 'var(--text-warning)' : 'var(--status-success)' }}></div>
            <span className="theme-text-secondary" style={{ fontSize: '13px' }}>
              {updateStatus?.isRunning
                ? `Выполняется: ${updateStatus.jobType === 'full' ? 'полная синхронизация' : updateStatus.jobType === 'competition' ? 'синхронизация лиги' : 'инкрементальное обновление'}`
                : 'Готово к обновлению'}
            </span>
          </div>
          <div className="theme-text-muted" style={{ fontSize: '12px' }}>
            {updateStatus?.isRunning && updateStatus.startedAt
              ? `Запущено: ${new Date(updateStatus.startedAt).toLocaleTimeString('ru-RU')}`
              : updateStatus?.lastCompletedAt
              ? `Завершено: ${new Date(updateStatus.lastCompletedAt).toLocaleTimeString('ru-RU')}`
              : 'Никогда'}
          </div>
        </div>
      </Card>

      {/* Update All Matches (Incremental) */}
      <Card className="ls-mb-4">
        <h2 className="ls-h4 ls-mb-2">Обновить матчи (инкрементально)</h2>
        <p className="theme-text-secondary ls-mb-3" style={{ fontSize: '12px' }}>
          Инкрементальная синхронизация: загружает матчи за последние 7 дней и ближайшие 30 дней. Быстрая операция, используется кроном ежедневно.
        </p>
        <button
          onClick={handleUpdateAll}
          disabled={updateStatus?.isRunning || updateAllMatchesMutation.isPending}
          className="ls-btn"
          style={{ opacity: updateStatus?.isRunning || updateAllMatchesMutation.isPending ? 0.5 : 1 }}
        >
          {updateAllMatchesMutation.isPending ? (
            <span className="ls-flex ls-align-center ls-gap-2">
              <span className="animate-spin" style={{ width: '14px', height: '14px', border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%' }}></span>
              Обновление...
            </span>
          ) : (
            '🔄 Обновить все матчи'
          )}
        </button>
      </Card>

      {/* Full Sync All Matches */}
      <Card className="ls-mb-4" style={{ borderLeft: '4px solid var(--text-warning, #ff9800)' }}>
        <h2 className="ls-h4 ls-mb-2">Полная синхронизация</h2>
        <p className="theme-text-secondary ls-mb-3" style={{ fontSize: '12px' }}>
          Загружает ВСЕ матчи за весь сезон для каждой активной лиги. Тяжёлая операция — используйте только при необходимости (первоначальная загрузка, исправление пропущенных матчей).
        </p>
        <div className="ls-py-2 ls-px-3 ls-mb-3" style={{ background: 'var(--bg-status-warning, rgba(255, 193, 7, 0.1))', borderRadius: '8px' }}>
          <p className="theme-status-warning" style={{ fontSize: '12px' }}>
            <strong>{t('admin:warning')}:</strong> {t('admin:fullSyncWarning')}
          </p>
        </div>
        <button
          onClick={handleFullSync}
          data-testid="admin-full-sync-button"
          disabled={updateStatus?.isRunning || fullSyncMutation.isPending}
          className="ls-btn"
          style={{ opacity: updateStatus?.isRunning || fullSyncMutation.isPending ? 0.5 : 1, background: 'var(--text-warning, #ff9800)' }}
        >
          {fullSyncMutation.isPending ? (
            <span className="ls-flex ls-align-center ls-gap-2">
              <span className="animate-spin" style={{ width: '14px', height: '14px', border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%' }}></span>
              Полная синхронизация...
            </span>
          ) : (
            'Полная синхронизация всех матчей'
          )}
        </button>
      </Card>

      <LeagueFullSyncPanel leagues={competitions ?? []} enabled={isAuthenticated && isAdmin} />

      {/* Update by Competition */}
      {competitions && competitions.length > 0 && (
        <Card className="ls-mb-4">
          <h2 className="ls-h4 ls-mb-2">Обновить по лигам</h2>
          <p className="theme-text-secondary ls-mb-3" style={{ fontSize: '12px' }}>
            Обновляет матчи для выбранной лиги
          </p>
          <div className="ls-grid ls-col-2 ls-gap-2">
            {competitions.map((competition) => (
              <button
                key={competition.id}
                onClick={() => handleUpdateCompetition(competition.code)}
                disabled={updateStatus?.isRunning || updateCompetitionMutation.isPending}
                className="ls-py-2 ls-px-3 theme-bg-tertiary ls-text-left"
                style={{ borderRadius: '8px', border: '1px solid var(--border-color)', opacity: updateStatus?.isRunning || updateCompetitionMutation.isPending ? 0.5 : 1 }}
              >
                <div className="ls-label" style={{ fontSize: '13px' }}>{competition.name}</div>
                <div className="theme-text-muted" style={{ fontSize: '11px' }}>{competition.code}</div>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Last Result */}
      {lastResult && (
        <Card>
          <h2 className="ls-h4 ls-mb-3">Результат последнего обновления</h2>
          <div className="ls-py-3 ls-px-3" style={{ borderRadius: '8px', background: lastResult.errors > 0 ? 'var(--bg-error)' : 'var(--bg-status-success)' }}>
            <div className="ls-flex ls-align-center ls-gap-2 ls-mb-2">
              <span style={{ fontSize: '16px' }}>{lastResult.errors > 0 ? '⚠️' : '✅'}</span>
              <span className={`ls-label ${lastResult.errors > 0 ? 'theme-text-error' : 'theme-status-success'}`}>
                {lastResult.message}
              </span>
            </div>
            <div className={`${lastResult.errors > 0 ? 'theme-text-error' : 'theme-status-success'}`} style={{ fontSize: '12px' }}>
              Обновлено матчей: {lastResult.updated}
              {lastResult.errors > 0 && ` • Ошибок: ${lastResult.errors}`}
              {lastResult.league && ` • Лига: ${lastResult.league}`}
            </div>
            {lastResult.errorDetails && lastResult.errorDetails.length > 0 && (
              <div className="ls-mt-3">
                <div className="ls-label theme-text-error ls-mb-2">Детали ошибок:</div>
                {lastResult.errorDetails.map((error, index) => (
                  <div key={index} className="theme-text-error theme-bg-error ls-py-2 ls-px-2 ls-mb-1" style={{ borderRadius: '4px', fontSize: '11px' }}>
                    <strong>{error.league}:</strong> {error.error}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}
    </MainSection>
  );
};

export default MatchesTab;
